import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { PaperPlaneTilt, Storefront, CurrencyDollar, Package, Trash, ChartLineUp, Lightbulb, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-35 admin marketplace page.
 *
 * Two panes:
 *   1. Pricing schedule: rows = states (+ "_default"), columns = accident
 *      types. Admin edits cents-per-lead inline. State value "_default"
 *      is the fallback for any state without its own row. Similarly
 *      "_default" accident-type is the row-level fallback.
 *   2. Live listings + sold history: leads currently on the marketplace
 *      plus everything that's been purchased, with delist for the listed
 *      ones. Filters: all / listed / sold.
 */

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

// Column headers — same set the LandingPageLeadIn accepts + a wildcard "_default".
const ACCIDENT_TYPES = ["auto", "truck", "motorcycle", "rideshare", "other", "_default"];

const cents = (v) => (v == null || v === "" ? "" : String(v));
const toCents = (s) => {
  const n = parseInt((s || "").replace(/[^\d]/g, ""), 10);
  return Number.isFinite(n) ? n : null;
};

export default function AdminMarketplace() {
  return (
    <div className="p-2 overflow-x-hidden" data-testid="admin-marketplace">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Storefront size={22} weight="bold" />
        <h1 className="font-chivo font-black text-2xl md:text-3xl tracking-tighter uppercase">
          Lead Marketplace
        </h1>
        <div className="label-mono ml-2 hidden md:block">
          Orphan leads (no matching buyer) auto-list here · admins set the price
        </div>
      </div>

      <AnalyticsPane />
      <LifecyclePane />
      <PricingPane />
      <ListingsPane />
      <UnsoldInventoryPane />
      <MatchPreviewPane />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter-64 - Unsold Lead Lifecycle settings + dry-run
// ---------------------------------------------------------------------------

function LifecyclePane() {
  const [settings, setSettings] = useState(null);
  const [minSellCents, setMinSellCents] = useState(35000);
  const [bucketSchema, setBucketSchema] = useState([]);
  const [dryRun, setDryRun] = useState(null);
  const [dryRunError, setDryRunError] = useState(null);
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSettings = async () => {
    try {
      const r = await api.get("/admin/marketplace/lifecycle/settings");
      setSettings(r.data.settings);
      setMinSellCents(r.data.min_sell_cents);
      // Iter-64 production-readiness: single source of truth --
      // the Admin UI mirrors the backend-derived bucket schema so
      // filters can never drift from the persisted config.
      setBucketSchema(r.data.inventory_bucket_schema || []);
    } catch (err) {
      toast.error("Failed to load lifecycle settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadSettings();
    })();
    return () => { cancelled = true; };
  }, []);

  const patch = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const r = await api.put("/admin/marketplace/lifecycle/settings", settings);
      setSettings(r.data.settings);
      // Reload settings to refresh bucket schema too.
      await loadSettings();
      toast.success("Lifecycle settings saved");
    } catch (err) {
      const detail = err.response?.data?.detail || "Save failed";
      setSaveError(detail);
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const runDryRun = async () => {
    // Iter-64 production-readiness: dry-run is a POST that carries the
    // CURRENT (potentially unsaved) form state as `proposed_settings`.
    // Zero writes on the backend; invalid values surface 400.
    setDryRunBusy(true);
    setDryRunError(null);
    setDryRun(null);
    try {
      const r = await api.post(
        "/admin/marketplace/lifecycle/dry-run",
        { proposed_settings: settings },
      );
      setDryRun(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail || "Dry run failed";
      setDryRunError(detail);
      toast.error(detail);
    } finally {
      setDryRunBusy(false);
    }
  };

  if (loading || !settings) {
    return <div className="p-4 label-mono">Loading lifecycle settings…</div>;
  }

  const blockedInput = (settings.marketplace_blocked_states || []).join(", ");

  return (
    <section className="border border-black bg-white p-4 mt-6" data-testid="lifecycle-pane">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Package size={18} weight="bold" />
        <h2 className="font-chivo font-black text-lg uppercase tracking-tight">
          Unsold Lead Lifecycle
        </h2>
        <span
          className="label-mono px-2 py-0.5 border border-black"
          data-testid="lifecycle-status-badge"
        >
          {settings.enabled ? "ENABLED" : "DISABLED"}
        </span>
      </div>
      <p className="text-xs text-neutral-700 mb-4 max-w-3xl">
        Leads expire from the active buyer marketplace after the configured
        period but remain safely stored in Unsold Inventory. Automatic price
        reductions never go below the Marketplace Minimum Sell Price (currently
        ${(minSellCents / 100).toFixed(2)}).
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!settings.enabled}
            onChange={(e) => patch("enabled", e.target.checked)}
            data-testid="lifecycle-enabled-toggle"
          />
          Lifecycle Enabled
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!settings.include_existing_listings}
              onChange={(e) => patch("include_existing_listings", e.target.checked)}
              data-testid="lifecycle-include-existing-toggle"
            />
            Include Existing Listings in Impact Preview
          </span>
          <span
            className="text-[10px] text-neutral-500 pl-6 leading-snug"
            data-testid="lifecycle-include-existing-help"
          >
            Preview scope only. Pre-existing listings remain grandfathered and
            are NEVER automatically adopted into the lifecycle by save or by
            the scheduler.
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!settings.price_adjustments_enabled}
            onChange={(e) => patch("price_adjustments_enabled", e.target.checked)}
            data-testid="lifecycle-price-adjust-toggle"
          />
          Automatic Price Adjustments
        </label>
        <NumField label="Aging After Hours" value={settings.aging_after_hours}
          onChange={(v) => patch("aging_after_hours", v)}
          testId="lifecycle-aging-hours" />
        <NumField label="Last Chance After Hours" value={settings.last_chance_after_hours}
          onChange={(v) => patch("last_chance_after_hours", v)}
          testId="lifecycle-last-chance-hours" />
        <NumField label="Active Marketplace Expires (h)"
          value={settings.expire_active_marketplace_after_hours}
          onChange={(v) => patch("expire_active_marketplace_after_hours", v)}
          testId="lifecycle-expire-hours" />
        <NumField label="Aging Price %" value={settings.aging_price_percent}
          onChange={(v) => patch("aging_price_percent", v)}
          testId="lifecycle-aging-pct" />
        <NumField label="Last Chance Price %" value={settings.last_chance_price_percent}
          onChange={(v) => patch("last_chance_price_percent", v)}
          testId="lifecycle-last-chance-pct" />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!settings.notify_buyers_on_price_drop}
            onChange={(e) => patch("notify_buyers_on_price_drop", e.target.checked)}
            data-testid="lifecycle-notify-price-drop"
          />
          Price Drop Buyer Alerts
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!settings.notify_buyers_on_last_chance}
            onChange={(e) => patch("notify_buyers_on_last_chance", e.target.checked)}
            data-testid="lifecycle-notify-last-chance"
          />
          Last Chance Buyer Alerts
        </label>
        <TextField
          label="Long-Term Bucket Days (comma-separated)"
          value={(settings.inventory_bucket_days || []).join(", ")}
          onChange={(v) => patch(
            "inventory_bucket_days",
            v.split(",").map((n) => parseInt(n.trim(), 10)).filter(Number.isFinite),
          )}
          testId="lifecycle-bucket-days"
        />
        <TextField
          label="Marketplace Blocked States (comma-separated)"
          value={blockedInput}
          onChange={(v) => patch(
            "marketplace_blocked_states",
            v.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
          )}
          testId="lifecycle-blocked-states"
        />
      </div>

      {saveError && (
        <div
          className="mt-3 border border-red-600 bg-red-50 text-red-800 text-xs px-3 py-2"
          data-testid="lifecycle-save-error"
        >
          {saveError}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-4">
        <button
          className="btn btn-primary label-mono uppercase px-4 py-1.5"
          onClick={save}
          disabled={saving}
          data-testid="lifecycle-save-btn"
        >
          {saving ? "Saving…" : "Save Settings"}
        </button>
        <button
          className="btn btn-outline label-mono uppercase px-4 py-1.5 border border-black"
          onClick={runDryRun}
          disabled={dryRunBusy}
          data-testid="lifecycle-dry-run-btn"
        >
          {dryRunBusy ? "Previewing…" : "Preview Lifecycle Impact"}
        </button>
      </div>

      {dryRunError && (
        <div
          className="mt-4 border border-red-600 bg-red-50 text-red-800 text-xs px-3 py-2"
          data-testid="lifecycle-dry-run-error"
        >
          {dryRunError}
        </div>
      )}

      {dryRun && (
        <div className="mt-4 border border-black p-3" data-testid="lifecycle-dry-run-result">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="label-mono uppercase">Dry Run</h3>
            {dryRun.proposed_overlay_applied && (
              <span
                className="label-mono uppercase text-[10px] px-1.5 py-0.5 border border-black bg-amber-50"
                data-testid="lifecycle-dry-run-overlay-flag"
              >
                UNSAVED SETTINGS PREVIEWED
              </span>
            )}
          </div>
          <pre className="text-xs whitespace-pre-wrap">
            {JSON.stringify({
              active: dryRun.active_counts,
              inventory: dryRun.inventory_counts,
              impact: dryRun.impact,
            }, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
}

function NumField({ label, value, onChange, testId }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="label-mono uppercase text-[10px]">{label}</span>
      <input
        type="number"
        className="input border border-black px-2 py-1 text-sm"
        value={value ?? ""}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        data-testid={testId}
      />
    </label>
  );
}

function TextField({ label, value, onChange, testId }) {
  return (
    <label className="flex flex-col gap-1 text-xs col-span-2">
      <span className="label-mono uppercase text-[10px]">{label}</span>
      <input
        type="text"
        className="input border border-black px-2 py-1 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Iter-64 - Unsold Inventory table (expired listings)
// ---------------------------------------------------------------------------

function UnsoldInventoryPane() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bucket, setBucket] = useState("");
  const [accidentBucket, setAccidentBucket] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [accidentTypeFilter, setAccidentTypeFilter] = useState("");
  // Iter-64 production-readiness: single source of truth -- the
  // long-term bucket options come from the backend so the UI cannot
  // drift from the persisted config (`inventory_bucket_days`).
  const [bucketSchema, setBucketSchema] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/marketplace/lifecycle/settings");
        if (!cancelled) setBucketSchema(r.data.inventory_bucket_schema || []);
      } catch (err) {
        /* non-fatal -- the UI will just show an empty options list */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [s, inv] = await Promise.all([
        api.get("/admin/marketplace/inventory/summary"),
        api.get("/admin/marketplace/inventory", {
          params: {
            ...(bucket ? { marketplace_bucket: bucket } : {}),
            ...(accidentBucket ? { accident_bucket: accidentBucket } : {}),
            ...(stateFilter ? { state: stateFilter } : {}),
            ...(accidentTypeFilter ? { accident_type: accidentTypeFilter } : {}),
          },
        }),
      ]);
      setSummary(s.data);
      setRows(inv.data.leads || []);
    } catch (err) {
      toast.error("Failed to load unsold inventory");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [bucket, accidentBucket, stateFilter, accidentTypeFilter]);

  const relist = async (leadId) => {
    if (!window.confirm(`Relist lead ${leadId}? A new marketplace cycle begins; consumer data is untouched.`)) return;
    try {
      await api.post(`/admin/marketplace/list-lead/${leadId}/relist`);
      toast.success("Lead relisted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Relist failed");
    }
  };

  return (
    <section className="border border-black bg-white p-4 mt-6" data-testid="unsold-inventory-pane">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Package size={18} weight="bold" />
        <h2 className="font-chivo font-black text-lg uppercase tracking-tight">
          Unsold Inventory
        </h2>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
          <SummaryCard title="Fresh" value={summary.active_counts?.fresh ?? 0} testId="card-fresh" />
          <SummaryCard title="Aging" value={summary.active_counts?.aging ?? 0} testId="card-aging" />
          <SummaryCard title="Last Chance" value={summary.active_counts?.last_chance ?? 0} testId="card-last-chance" />
          <SummaryCard title="Unsold Total" value={summary.total_unsold_inventory ?? 0} testId="card-unsold-total" />
          <SummaryCard title="Unsold Ask $" testId="card-unsold-ask"
            value={`$${((summary.unsold_inventory_ask_value_cents ?? 0) / 100).toFixed(2)}`}
            subtitle="NOT booked revenue" />
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-3">
        <select
          className="border border-black px-2 py-1 text-sm"
          value={bucket}
          onChange={(e) => setBucket(e.target.value)}
          data-testid="filter-marketplace-bucket"
        >
          <option value="">All Marketplace Ages</option>
          {bucketSchema.map((b) => (
            <option key={b.key} value={b.key}>{b.label}</option>
          ))}
        </select>
        <select
          className="border border-black px-2 py-1 text-sm"
          value={accidentBucket}
          onChange={(e) => setAccidentBucket(e.target.value)}
          data-testid="filter-accident-bucket"
        >
          <option value="">All Accident Ages</option>
          <option value="within_30_days">Within 30 Days</option>
          <option value="31_to_60_days">31-60 Days</option>
          <option value="61_to_90_days">61-90 Days</option>
          <option value="91_to_120_days">91-120 Days</option>
          <option value="121_days_to_1_year">121 Days-1 Year</option>
          <option value="over_1_year">Over 1 Year</option>
          <option value="91_days_to_1_year_broad">91 Days-1 Year (broad bucket)</option>
          <option value="unknown">Unknown</option>
        </select>
        <select
          className="border border-black px-2 py-1 text-sm"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          data-testid="filter-state"
        >
          <option value="">All States</option>
          {STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <select
          className="border border-black px-2 py-1 text-sm"
          value={accidentTypeFilter}
          onChange={(e) => setAccidentTypeFilter(e.target.value)}
          data-testid="filter-accident-type"
        >
          <option value="">All Accident Types</option>
          <option value="auto">auto</option>
          <option value="truck">truck</option>
          <option value="motorcycle">motorcycle</option>
          <option value="rideshare">rideshare</option>
          <option value="pedestrian">pedestrian</option>
          <option value="bicycle">bicycle</option>
          <option value="other">other</option>
        </select>
      </div>

      {loading && <div className="label-mono text-xs">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="label-mono text-xs text-neutral-600" data-testid="unsold-inventory-empty">
          No unsold inventory matches these filters.
        </div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="unsold-inventory-table">
            <thead>
              <tr className="text-left border-b border-black">
                <th className="p-2">Lead</th>
                <th className="p-2">State</th>
                <th className="p-2">Type</th>
                <th className="p-2">Marketplace Age</th>
                <th className="p-2">Accident Age</th>
                <th className="p-2">Last Ask</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-300"
                    data-testid={`inv-row-${r.id}`}>
                  <td className="p-2 font-mono">{r.id.slice(0, 12)}…</td>
                  <td className="p-2">{r.state || "—"}</td>
                  <td className="p-2">{r.accident_type || "—"}</td>
                  <td className="p-2">
                    {r._marketplace_bucket} · {Math.round(r._marketplace_age_hours / 24)}d
                  </td>
                  <td className="p-2">{r._accident_age?.label || "—"}</td>
                  <td className="p-2 font-mono">
                    ${((r.marketplace_price || 0) / 100).toFixed(2)}
                  </td>
                  <td className="p-2">
                    <button
                      onClick={() => relist(r.id)}
                      data-testid={`relist-${r.id}`}
                      className="btn btn-outline label-mono uppercase px-2 py-0.5 border border-black text-[10px]"
                    >
                      Relist
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SummaryCard({ title, value, subtitle, testId }) {
  return (
    <div className="border border-black p-3" data-testid={testId}>
      <div className="label-mono uppercase text-[10px] mb-1">{title}</div>
      <div className="text-lg font-black font-chivo">{value}</div>
      {subtitle && (
        <div className="text-[10px] text-neutral-600 mt-0.5">{subtitle}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter-64 production-readiness -- Buyer -> Unsold Inventory Match Preview
// ---------------------------------------------------------------------------

/**
 * Iter-64 FINAL SOURCE REVIEW: fetch every page of a paginated admin
 * directory endpoint (respecting the backend's ``page_size <= 100``
 * clamp). Never stops at row 100. The directory endpoints all return
 * ``{items, pagination: {has_next, ...}}``; when ``pagination`` is
 * missing we assume the response is complete.
 *
 * ``itemsKey`` names the array in the response (``organizations`` or
 * ``destinations``). ``maxPages`` guards against runaway loops --
 * 10,000 rows is far beyond current fleet size and only prevents an
 * infinite loop if the backend returns ``has_next=true`` forever.
 */
async function fetchAllPages(url, baseParams, itemsKey, maxPages = 100) {
  const all = [];
  let page = 1;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const r = await api.get(url, {
      params: { ...(baseParams || {}), page, page_size: 100 },
    });
    const items = (r.data && r.data[itemsKey]) || [];
    for (const it of items) all.push(it);
    const has_next = r.data && r.data.pagination && r.data.pagination.has_next;
    if (!has_next) break;
    page += 1;
    if (page > maxPages) break;
  }
  return all;
}

function MatchPreviewPane() {
  const [clients, setClients] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [clientId, setClientId] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Iter-64 FINAL SOURCE REVIEW: walk every page (page_size=100
        // is the backend max) so the selector cannot silently omit
        // buyer organisations / destinations beyond row 100.
        const [orgs, dests] = await Promise.all([
          fetchAllPages("/organizations", { type: "client" }, "organizations"),
          fetchAllPages(
            "/delivery/destinations",
            // ACTIVE destinations only -- the match preview must not
            // offer paused destinations as onboarding candidates.
            { status: "active" },
            "destinations",
          ),
        ]);
        if (cancelled) return;
        setClients(orgs);
        setDestinations(dests);
      } catch (err) {
        /* non-fatal -- the admin can still type a destination_id manually */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Iter-64 FINAL SOURCE REVIEW: destination choices are ALWAYS scoped
  // to the currently-selected buyer organisation. Selecting a
  // destination must never widen the visible set (would let Buyer B's
  // destinations appear after clicking a Buyer A destination).
  const filteredDests = destinations.filter(
    (d) => (!clientId || d.client_id === clientId)
      && d.is_active !== false,
  );

  const runPreview = async () => {
    if (!clientId && !destinationId) {
      setError("Select a buyer organization or destination");
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const payload = {};
      if (destinationId) payload.destination_id = destinationId;
      if (clientId) payload.client_id = clientId;
      const r = await api.post(
        "/admin/marketplace/inventory/match-preview",
        payload,
      );
      setResult(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail || "Match preview failed";
      setError(detail);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="border border-black bg-white p-4 mt-6"
      data-testid="match-preview-pane"
    >
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <Storefront size={18} weight="bold" />
        <h2 className="font-chivo font-black text-lg uppercase tracking-tight">
          New Buyer &rarr; Unsold Inventory Match
        </h2>
        <span
          className="label-mono uppercase text-[10px] px-1.5 py-0.5 border border-black bg-amber-50"
          data-testid="match-preview-zero-write-flag"
        >
          ZERO WRITES
        </span>
      </div>
      <p className="text-xs text-neutral-700 mb-4 max-w-3xl">
        Dry-run against currently expired unsold inventory. No leads are
        assigned, marked sold, relisted, or exposed to the buyer &mdash;
        buyer alerts are NOT sent. Use this before onboarding a new law-firm
        buyer to size the immediate inventory match.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="label-mono uppercase text-[10px]">Buyer Organization</span>
          <select
            className="input border border-black px-2 py-1 text-sm"
            value={clientId}
            onChange={(e) => { setClientId(e.target.value); setDestinationId(""); }}
            data-testid="match-preview-client-select"
          >
            <option value="">(select buyer org)</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name || c.id}</option>
            ))}
          </select>
          <span className="text-[10px] text-neutral-500">
            All active destinations for this buyer are evaluated. A lead is a
            match if AT LEAST ONE active destination is eligible.
          </span>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="label-mono uppercase text-[10px]">
            OR Specific Destination (optional)
          </span>
          <select
            className="input border border-black px-2 py-1 text-sm"
            value={destinationId}
            onChange={(e) => setDestinationId(e.target.value)}
            data-testid="match-preview-destination-select"
          >
            <option value="">(any active destination for the org)</option>
            {filteredDests.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name || d.id}
                {d.is_active === false ? " (inactive)" : ""}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap gap-3 mt-4">
        <button
          className="btn btn-primary label-mono uppercase px-4 py-1.5 border border-black"
          onClick={runPreview}
          disabled={busy || (!clientId && !destinationId)}
          data-testid="match-preview-run-btn"
        >
          {busy ? "Previewing…" : "Run Match Preview"}
        </button>
      </div>

      {error && (
        <div
          className="mt-3 border border-red-600 bg-red-50 text-red-800 text-xs px-3 py-2"
          data-testid="match-preview-error"
        >
          {error}
        </div>
      )}

      {result && (
        <div
          className="mt-4 border border-black p-3"
          data-testid="match-preview-result"
        >
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <h3 className="label-mono uppercase text-xs">Preview</h3>
            <span
              className="label-mono uppercase text-[10px] px-1.5 py-0.5 border border-black bg-amber-50"
            >
              ZERO WRITES
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <SummaryCard
              title="Matches"
              value={result.matches ?? 0}
              testId="match-preview-matches"
            />
            <SummaryCard
              title="Considered"
              value={result.considered ?? 0}
              testId="match-preview-considered"
            />
            <SummaryCard
              title="Active Destinations"
              value={result.active_destinations ?? 0}
              testId="match-preview-active-dests"
            />
            <SummaryCard
              title="Ineligible Reasons"
              value={Object.keys(result.ineligible_reasons || {}).length}
              testId="match-preview-reason-count"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <div className="label-mono uppercase text-[10px] mb-1">
                Marketplace Age Breakdown
              </div>
              <pre
                className="text-xs whitespace-pre-wrap border border-black p-2"
                data-testid="match-preview-mkt-breakdown"
              >
                {JSON.stringify(result.marketplace_age_breakdown || {}, null, 2)}
              </pre>
            </div>
            <div>
              <div className="label-mono uppercase text-[10px] mb-1">
                Accident Age Breakdown
              </div>
              <pre
                className="text-xs whitespace-pre-wrap border border-black p-2"
                data-testid="match-preview-acc-breakdown"
              >
                {JSON.stringify(result.accident_age_breakdown || {}, null, 2)}
              </pre>
            </div>
          </div>
          {Object.keys(result.ineligible_reasons || {}).length > 0 && (
            <div className="mt-3">
              <div className="label-mono uppercase text-[10px] mb-1">
                Top Ineligible Reasons
              </div>
              <pre
                className="text-xs whitespace-pre-wrap border border-black p-2"
                data-testid="match-preview-ineligible"
              >
                {JSON.stringify(result.ineligible_reasons || {}, null, 2)}
              </pre>
            </div>
          )}
          {result.note && (
            <div
              className="text-[11px] text-neutral-600 mt-3 italic"
              data-testid="match-preview-note"
            >
              {result.note}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sold-Through Analytics pane (iter-36)
// ---------------------------------------------------------------------------

function fmtTTP(secs) {
  if (secs == null) return "—";
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  if (secs < 86400) return `${(secs / 3600).toFixed(1)}h`;
  return `${(secs / 86400).toFixed(1)}d`;
}

function fmtPct(v) {
  return v == null ? "—" : `${v.toFixed(1)}%`;
}

/**
 * 30-day sold-through sparkline.
 *
 * Each bucket is one UTC day. Bar height (grey) is proportional to `listed`;
 * a green sub-bar overlays the portion that sold within 24 hours of listing.
 * A tall mostly-grey bar means listings piled up but few sold in 24h — pricing
 * likely needs a nudge down. A short green bar means sales matched supply.
 * A run of empty days at the tail means the state has gone quiet.
 */
function Sparkline({ daily, width = 120, height = 28, testId }) {
  const days = daily || [];
  if (!days.length) {
    return (
      <span className="text-[10px] text-zinc-400 font-mono" data-testid={testId}>
        —
      </span>
    );
  }
  const maxListed = Math.max(1, ...days.map((d) => d.listed || 0));
  const gap = 1;
  const barW = Math.max(1, (width - gap * (days.length - 1)) / days.length);

  // Aggregate stats for the tooltip / a11y label.
  const totalListed = days.reduce((s, d) => s + (d.listed || 0), 0);
  const totalSold = days.reduce((s, d) => s + (d.sold_within_24h || 0), 0);
  const soldPct = totalListed ? Math.round((totalSold / totalListed) * 100) : 0;
  const label = `${days.length}d trend: ${totalListed} listed, ${totalSold} sold within 24h (${soldPct}%)`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
      data-testid={testId}
      className="align-middle"
    >
      <title>{label}</title>
      {/* Baseline */}
      <line x1="0" y1={height - 0.5} x2={width} y2={height - 0.5} stroke="#e4e4e7" strokeWidth="1" />
      {days.map((d, i) => {
        const listed = d.listed || 0;
        const sold = Math.min(d.sold_within_24h || 0, listed);
        if (!listed) {
          // Show a faint 1px dot on empty days so the sparkline reads as "date axis".
          return (
            <rect
              key={d.date || i}
              x={i * (barW + gap)}
              y={height - 1}
              width={barW}
              height={1}
              fill="#e4e4e7"
            />
          );
        }
        const totalH = Math.max(1, Math.round((listed / maxListed) * (height - 2)));
        const soldH = listed ? Math.round((sold / listed) * totalH) : 0;
        const y = height - totalH;
        return (
          <g key={d.date || i} transform={`translate(${i * (barW + gap)}, 0)`}>
            {/* Grey unsold portion */}
            <rect x="0" y={y} width={barW} height={totalH} fill="#a1a1aa" />
            {/* Green sold-within-24h portion overlaid at bottom */}
            {soldH > 0 && (
              <rect x="0" y={height - soldH} width={barW} height={soldH} fill="#059669" />
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AnalyticsPane() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Underperforming-pricing threshold. Rows with 24h sold-through % below this
  // AND enough mature listings to be statistically meaningful get a red pulse.
  // Persisted in localStorage so an admin's preference sticks between sessions.
  const [threshold, setThreshold] = useState(() => {
    const raw = Number(localStorage.getItem("mp:soldThroughThreshold"));
    return Number.isFinite(raw) && raw > 0 && raw <= 100 ? raw : 30;
  });
  // Ignore states with tiny sample size — 1-2 listings shouldn't set off alarms.
  const MIN_SAMPLES = 3;

  useEffect(() => {
    localStorage.setItem("mp:soldThroughThreshold", String(threshold));
  }, [threshold]);

  useEffect(() => {
    api.get("/admin/marketplace/analytics")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  const overall = data?.overall || {};
  const w24 = overall.window_24h || {};
  const w7 = overall.window_7d || {};
  const rows = data?.by_state || [];

  const isUnderperforming = (s) => {
    const w = s.window_24h || {};
    const pct = w.sold_through_pct;
    const mature = w.listed_mature || 0;
    if (pct == null) return false;
    if (mature < MIN_SAMPLES) return false;
    return pct < threshold;
  };
  const flaggedStates = useMemo(
    () => rows.filter(isUnderperforming),
    [rows, threshold],
  );

  return (
    <div className="brut-card mb-8" data-testid="mp-analytics-pane">
      <div className="border-b border-black px-5 py-3 flex items-center gap-2 flex-wrap">
        <ChartLineUp size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Sold-through analytics
        </div>
        <div className="label-mono ml-2">
          Trailing 30 days · {data?.listings_in_horizon ?? "…"} listing{data?.listings_in_horizon === 1 ? "" : "s"}
        </div>
        {!loading && flaggedStates.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const first = document.querySelector(
                `[data-testid="mp-analytics-row-${flaggedStates[0].state}"]`,
              );
              first?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            data-testid="mp-analytics-needs-attention"
            title={`States below ${threshold}% sold-through in 24h: ${flaggedStates.map((s) => s.state).join(", ")}`}
            className="ml-2 inline-flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 border border-red-600 hover:bg-red-700 animate-pulse"
          >
            <WarningCircle size={11} weight="fill" />
            {flaggedStates.length} state{flaggedStates.length === 1 ? "" : "s"} need pricing attention
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-600 whitespace-nowrap">
            Alert below
            <input
              type="number"
              min="1" max="99" step="1"
              value={threshold}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v >= 1 && v <= 99) setThreshold(v);
              }}
              data-testid="mp-analytics-threshold-input"
              className="w-14 border border-black px-2 py-1 font-mono text-xs text-right"
            />
            % sold in 24h
          </label>
        </div>
      </div>

      {/* Overall — big numbers */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-b border-black">
        <div className="p-5 border-r border-black" data-testid="mp-analytics-overall-24h-pct">
          <div className="label-mono">Sold within 24h</div>
          <div className="font-chivo font-black text-4xl mt-1">
            {loading ? "…" : fmtPct(w24.sold_through_pct)}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 font-mono">
            {w24.sold_within ?? 0} / {w24.listed_mature ?? 0} mature listings
          </div>
        </div>
        <div className="p-5 border-r border-black" data-testid="mp-analytics-overall-24h-ttp">
          <div className="label-mono">Avg time-to-buy (24h)</div>
          <div className="font-chivo font-black text-4xl mt-1">
            {loading ? "…" : fmtTTP(w24.avg_time_to_purchase_secs)}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 font-mono">
            based on 24h-window sales
          </div>
        </div>
        <div className="p-5 border-r border-black bg-zinc-50" data-testid="mp-analytics-overall-7d-pct">
          <div className="label-mono">Sold within 7d</div>
          <div className="font-chivo font-black text-4xl mt-1">
            {loading ? "…" : fmtPct(w7.sold_through_pct)}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 font-mono">
            {w7.sold_within ?? 0} / {w7.listed_mature ?? 0} mature listings
          </div>
        </div>
        <div className="p-5 bg-zinc-50" data-testid="mp-analytics-overall-7d-ttp">
          <div className="label-mono">Avg time-to-buy (7d)</div>
          <div className="font-chivo font-black text-4xl mt-1">
            {loading ? "…" : fmtTTP(w7.avg_time_to_purchase_secs)}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 font-mono">
            based on 7d-window sales
          </div>
        </div>
      </div>

      {/* Overall 30-day trend */}
      {!loading && overall.daily_series?.length > 0 && (
        <div
          className="border-b border-black px-5 py-3 flex items-center gap-4 flex-wrap bg-white"
          data-testid="mp-analytics-overall-trend"
        >
          <div>
            <div className="label-mono">30-day trend</div>
            <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
              Grey bar = leads listed that day · Green fill = sold within 24h
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
              <span className="inline-block w-2.5 h-2.5 bg-zinc-400" /> listed
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
              <span className="inline-block w-2.5 h-2.5 bg-emerald-600" /> sold ≤24h
            </span>
            <Sparkline
              daily={overall.daily_series}
              width={260}
              height={40}
              testId="mp-analytics-overall-sparkline"
            />
          </div>
        </div>
      )}

      {/* Per-state breakdown */}
      <div className="p-5 overflow-x-auto">
        {loading && <div className="label-mono text-center py-4">Loading analytics…</div>}
        {!loading && !rows.length && (
          <div className="label-mono text-center py-4 text-zinc-500">
            No marketplace activity in the last 30 days.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <table className="w-full text-xs" data-testid="mp-analytics-by-state">
            <thead>
              <tr className="border-b-2 border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 px-3">State</th>
                <th className="text-right py-2 px-3">Listed</th>
                <th className="text-right py-2 px-3">Sold</th>
                <th className="text-right py-2 px-3 border-l border-black">24h %</th>
                <th className="text-right py-2 px-3">24h avg TTP</th>
                <th className="text-right py-2 px-3 border-l border-black bg-zinc-200">7d %</th>
                <th className="text-right py-2 px-3 bg-zinc-200">7d avg TTP</th>
                <th className="text-right py-2 px-3 border-l border-black">Trend (30d)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const flagged = isUnderperforming(s);
                const w = s.window_24h || {};
                const tip = flagged
                  ? `24h sold-through ${fmtPct(w.sold_through_pct)} < ${threshold}% threshold — consider lowering price`
                  : undefined;
                return (
                  <tr
                    key={s.state}
                    className={
                      "border-b border-zinc-200 " +
                      (flagged ? "bg-red-50/60" : "")
                    }
                    data-testid={`mp-analytics-row-${s.state}`}
                    data-flagged={flagged ? "1" : "0"}
                    title={tip}
                  >
                    <td className="py-1.5 px-3 font-mono font-bold">
                      <span className="inline-flex items-center gap-1.5">
                        {flagged && (
                          <span
                            className="relative inline-flex h-2 w-2"
                            data-testid={`mp-flag-${s.state}`}
                          >
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                          </span>
                        )}
                        {s.state}
                      </span>
                    </td>
                    <td className="py-1.5 px-3 text-right font-mono">{s.listed_total}</td>
                    <td className="py-1.5 px-3 text-right font-mono">{s.sold_total}</td>
                    <td className={
                      "py-1.5 px-3 text-right font-mono font-bold border-l border-black " +
                      (flagged ? "text-red-700" : "")
                    }>
                      {fmtPct(w.sold_through_pct)}
                    </td>
                    <td className="py-1.5 px-3 text-right font-mono text-[11px] text-zinc-600">
                      {fmtTTP(w.avg_time_to_purchase_secs)}
                    </td>
                    <td className="py-1.5 px-3 text-right font-mono font-bold border-l border-black bg-zinc-50">
                      {fmtPct(s.window_7d?.sold_through_pct)}
                    </td>
                    <td className="py-1.5 px-3 text-right font-mono text-[11px] text-zinc-600 bg-zinc-50">
                      {fmtTTP(s.window_7d?.avg_time_to_purchase_secs)}
                    </td>
                    <td className="py-1 px-3 text-right border-l border-black">
                      <Sparkline
                        daily={s.daily_series}
                        testId={`mp-analytics-sparkline-${s.state}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pricing table pane
// ---------------------------------------------------------------------------

function PricingPane() {
  const [pricing, setPricing] = useState({});
  const [defaultCents, setDefaultCents] = useState(5000);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAllStates, setShowAllStates] = useState(false);
  const [suggestions, setSuggestions] = useState({});

  const load = () =>
    api.get("/admin/marketplace/pricing")
      .then((r) => {
        setPricing(r.data.pricing || {});
        setDefaultCents(r.data.default_cents || 35000);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load pricing"));

  const loadSuggestions = () =>
    api.get("/admin/marketplace/pricing/suggestions")
      .then((r) => setSuggestions(r.data.suggestions || {}))
      .catch(() => { /* non-fatal — chips just won't show */ });

  useEffect(() => { load(); loadSuggestions(); }, []);

  const setCell = (st, at, val) => {
    setDirty(true);
    setPricing((p) => {
      const next = { ...p };
      const row = { ...(next[st] || {}) };
      if (val == null || val === "") {
        delete row[at];
      } else {
        row[at] = val;
      }
      if (Object.keys(row).length === 0) delete next[st];
      else next[st] = row;
      return next;
    });
  };

  const acceptSuggestion = (st, at, cents) => {
    setCell(st, at, cents);
    toast.success(`Applied $${(cents / 100).toFixed(2)} to ${st} / ${at}`);
  };

  const acceptAllForState = (st) => {
    const stSug = suggestions[st] || {};
    Object.entries(stSug).forEach(([at, sug]) => {
      // Only fill cells that don't already have a value.
      if (!(pricing[st] || {})[at] && sug?.cents) {
        setCell(st, at, sug.cents);
      }
    });
    toast.success(`Applied suggestions for ${st}`);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/marketplace/pricing", { pricing });
      toast.success("Marketplace pricing saved");
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  // States that already have a schedule + states with suggestions + always "_default" up top.
  const configuredStates = useMemo(
    () => Object.keys(pricing).filter((s) => s !== "_default").sort(),
    [pricing],
  );
  const suggestedStates = useMemo(
    () => Object.keys(suggestions).filter((s) => s !== "_default").sort(),
    [suggestions],
  );
  const visibleStates = showAllStates
    ? ["_default", ...STATES]
    : ["_default", ...Array.from(new Set([...configuredStates, ...suggestedStates])).sort()];

  return (
    <div className="brut-card mb-8" data-testid="mp-pricing-pane">
      <div className="border-b border-black px-5 py-3 flex items-center gap-2 flex-wrap">
        <CurrencyDollar size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">Pricing schedule</div>
        <div className="label-mono ml-2">
          Prices are per-lead, in <b>cents</b>. Blank = fall through to _default row / column.
        </div>
        <div className="ml-auto flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest cursor-pointer">
            <input
              type="checkbox"
              checked={showAllStates}
              onChange={(e) => setShowAllStates(e.target.checked)}
              data-testid="mp-show-all-states"
              className="accent-black"
            />
            Show all states
          </label>
          <button
            onClick={save}
            disabled={!dirty || busy}
            data-testid="mp-pricing-save"
            className="border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-black hover:text-white"
          >
            {busy ? "Saving…" : dirty ? "Save changes" : "No changes"}
          </button>
        </div>
      </div>

      <div className="p-5 overflow-x-auto">
        <div className="text-[11px] text-zinc-500 mb-3 leading-relaxed max-w-2xl">
          Hard fallback when no cell + no defaults are set:{" "}
          <b>${(defaultCents / 100).toFixed(2)}</b> per lead. Enter values in
          <b> cents</b> (e.g. 10000 = $100.00). Yellow <Lightbulb size={11} weight="fill" className="inline align-text-bottom text-amber-500" />{" "}
          chips are one-click auto-suggestions from sold history (≥5 sales) or state_fees averages.
        </div>
        <table className="w-full text-xs border border-black" data-testid="mp-pricing-table">
          <thead>
            <tr className="border-b-2 border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
              <th className="text-left py-2 px-3 border-r border-black">State</th>
              {ACCIDENT_TYPES.map((at) => (
                <th key={at} className={`text-right py-2 px-3 border-r border-black last:border-r-0 ${at === "_default" ? "bg-amber-50" : ""}`}>
                  {at}
                </th>
              ))}
              <th className="text-right py-2 px-3 border-l-2 border-black text-[9px]">Apply row</th>
            </tr>
          </thead>
          <tbody>
            {visibleStates.map((st) => {
              const stSug = suggestions[st] || {};
              const hasAnySuggestionUnused = ACCIDENT_TYPES.some(
                (at) => stSug[at] && !(pricing[st] || {})[at],
              );
              return (
                <tr
                  key={st}
                  className={`border-b border-zinc-200 ${st === "_default" ? "bg-amber-50/40" : ""}`}
                  data-testid={`mp-pricing-row-${st}`}
                >
                  <td className="py-1.5 px-3 border-r border-black font-mono font-bold">
                    {st}
                  </td>
                  {ACCIDENT_TYPES.map((at) => {
                    const val = (pricing[st] || {})[at];
                    const sug = stSug[at];
                    const showChip = sug && (val == null || val === "");
                    return (
                      <td
                        key={at}
                        className={`py-1 px-2 border-r border-zinc-200 last:border-r-0 relative ${at === "_default" ? "bg-amber-50/40" : ""}`}
                      >
                        <input
                          type="number"
                          min="0"
                          step="100"
                          value={cents(val)}
                          onChange={(e) => setCell(st, at, toCents(e.target.value))}
                          placeholder={sug ? `~${sug.cents}` : "—"}
                          data-testid={`mp-cell-${st}-${at}`}
                          className="w-full px-2 py-1 text-right font-mono text-[11px] border-none focus:outline-none focus:bg-yellow-100 bg-transparent"
                        />
                        {showChip && (
                          <button
                            type="button"
                            onClick={() => acceptSuggestion(st, at, sug.cents)}
                            data-testid={`mp-suggest-${st}-${at}`}
                            title={`Suggest: $${(sug.cents / 100).toFixed(2)} (${sug.source === "sold_history" ? `${sug.samples} sales` : `${sug.samples}-buyer state_fees avg`})`}
                            className="mt-0.5 w-full text-[9px] font-mono uppercase tracking-wider border border-amber-600 bg-amber-100 hover:bg-amber-300 text-amber-900 px-1 py-0.5 leading-tight inline-flex items-center justify-center gap-1"
                          >
                            <Lightbulb size={8} weight="fill" />
                            ${(sug.cents / 100).toFixed(0)}
                          </button>
                        )}
                      </td>
                    );
                  })}
                  <td className="py-1 px-2 border-l-2 border-black text-right">
                    {hasAnySuggestionUnused && (
                      <button
                        type="button"
                        onClick={() => acceptAllForState(st)}
                        data-testid={`mp-suggest-row-${st}`}
                        className="text-[9px] font-mono uppercase tracking-wider border border-amber-600 bg-amber-50 hover:bg-amber-200 text-amber-900 px-1.5 py-0.5"
                      >
                        Use all
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Listings pane
// ---------------------------------------------------------------------------

function ListingsPane() {
  const [filter, setFilter] = useState("listed");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/admin/marketplace/listings?status=${filter}`)
      .then((r) => setLeads(r.data.leads || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load listings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const delist = async (leadId) => {
    if (!window.confirm("Remove this lead from the marketplace?")) return;
    try {
      await api.delete(`/admin/marketplace/list-lead/${leadId}`);
      toast.success("Delisted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delist failed");
    }
  };

  const expireNow = async (leadId) => {
    if (!window.confirm(
      "Expire this listing from the active buyer marketplace now?\n\n" +
      "The lead will remain safely in Unsold Inventory (nothing is deleted, " +
      "rejected, delivered, or archived). Consumer / TCPA / TrustedForm " +
      "evidence stays intact."
    )) return;
    try {
      await api.post(`/admin/marketplace/list-lead/${leadId}/expire-now`);
      toast.success("Expired from active marketplace");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Expire failed");
    }
  };

  return (
    <div className="brut-card" data-testid="mp-listings-pane">
      <div className="border-b border-black px-5 py-3 flex items-center gap-2 flex-wrap">
        <Package size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Listings ({leads.length})
        </div>
        <div className="ml-auto flex gap-1">
          {["listed", "sold", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`mp-filter-${f}`}
              className={`border border-black px-2 py-1 text-[10px] uppercase tracking-widest ${
                filter === f ? "bg-black text-white" : "hover:bg-zinc-100"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading && <div className="p-8 label-mono text-center">Loading…</div>}
        {!loading && !leads.length && (
          <div className="p-8 label-mono text-center text-zinc-500">
            No leads {filter === "sold" ? "have been sold" : "on the marketplace"} yet.
          </div>
        )}
        {!loading && leads.length > 0 && (
          <table className="w-full text-xs" data-testid="mp-listings-table">
            <thead>
              <tr className="border-b border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">State</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">Landing</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Price</th>
                <th className="text-left py-2 px-3">Listed</th>
                <th className="text-left py-2 px-3">Sold</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr
                  key={l.id}
                  className="border-b border-zinc-200"
                  data-testid={`mp-listing-${l.id}`}
                >
                  <td className="py-2 px-3">
                    {l.first_name} {l.last_name?.slice(0, 1)}.
                  </td>
                  <td className="py-2 px-3 font-mono">{l.state}</td>
                  <td className="py-2 px-3 font-mono text-[11px]">{l.accident_type}</td>
                  <td className="py-2 px-3 font-mono text-[11px]">
                    {(l.extra || {}).landing_page || "—"}
                  </td>
                  <td className="py-2 px-3">
                    {l.marketplace_status === "sold" ? (
                      <span className="bg-emerald-700 text-white px-1.5 py-0.5 text-[9px] uppercase tracking-widest">
                        sold
                      </span>
                    ) : (
                      <span className="bg-amber-100 border border-amber-800 text-amber-900 px-1.5 py-0.5 text-[9px] uppercase tracking-widest">
                        listed
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right font-mono font-bold">
                    ${((l.marketplace_price || 0) / 100).toFixed(2)}
                  </td>
                  <td className="py-2 px-3 font-mono text-[10px] text-zinc-500">
                    {(l.marketplace_listed_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="py-2 px-3 font-mono text-[10px] text-zinc-500">
                    {(l.marketplace_sold_at || "").slice(0, 16).replace("T", " ") || "—"}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {l.marketplace_status === "listed" && (
                      <div className="inline-flex gap-1">
                        <Link
                          to={`/leads/${l.id}?tab=delivery`}
                          data-testid={`mp-send-to-buyer-${l.id}`}
                          className="border border-black bg-black text-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-zinc-800 inline-flex items-center gap-1"
                          title="Open this lead's Delivery tab to send it to a buyer"
                        >
                          <PaperPlaneTilt size={10} weight="bold" /> Send to buyer
                        </Link>
                        <button
                          onClick={() => expireNow(l.id)}
                          data-testid={`mp-expire-now-${l.id}`}
                          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-amber-500 hover:text-black hover:border-amber-500 inline-flex items-center gap-1"
                          title="Expire from active marketplace now (retained in Unsold Inventory)"
                        >
                          Expire Now
                        </button>
                        <button
                          onClick={() => delist(l.id)}
                          data-testid={`mp-delist-${l.id}`}
                          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-700 hover:text-white hover:border-red-700 inline-flex items-center gap-1"
                        >
                          <Trash size={10} weight="bold" /> Delist
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
